import { GameLoop } from "./game/loop";
import { Input } from "./game/input";
import { renderArenaIdle, renderDuel, toggleHitboxDebug } from "./game/render";
import { DuelController } from "./game/sync";
import {
  tipForPhase,
  TUTORIAL_ABILITIES_GATE,
  TUTORIAL_ABILITIES_TIP,
  TUTORIAL_TAUNT_AT_MS,
  TUTORIAL_TAUNT_GATE,
  TUTORIAL_TAUNT_TIP,
} from "./game/tutorial";
import { TutorialBot } from "./game/tutorialBot";
import { pauseCountdown, resumeAudio, resumeCountdown, startMenuMusic } from "./game/audio";
import { createLoopbackPair } from "./net/peerLink";
import { GameSession } from "./net/session";
import { Screens } from "./ui/screens";
import { loadPlayerSprites } from "./game/sprites";
import { assetUrl } from "./assetUrl";
import "./styles.css";

const FONT_URL = assetUrl("fonts/Pixel-Western.woff2");
const FONT_URL_FALLBACK = assetUrl("fonts/Pixel-Western.fixed.ttf");
const VCR_FONT_URL = assetUrl("fonts/VCR_OSD_MONO.ttf");

async function loadGameFont(): Promise<void> {
  try {
    let face = new FontFace("Pixel-Western", `url(${FONT_URL})`);
    try {
      const loaded = await face.load();
      document.fonts.add(loaded);
    } catch {
      face = new FontFace("Pixel-Western", `url(${FONT_URL_FALLBACK})`);
      const loaded = await face.load();
      document.fonts.add(loaded);
    }
    try {
      const vcr = new FontFace("VCR OSD Mono", `url(${VCR_FONT_URL})`);
      document.fonts.add(await vcr.load());
    } catch (err) {
      console.warn("Failed to load VCR OSD Mono font", err);
    }
    await document.fonts.ready;
  } catch (err) {
    console.warn("Failed to load Pixel-Western font", err);
  }
}

void loadGameFont();
void loadPlayerSprites();

const canvas = document.getElementById("game") as HTMLCanvasElement;
const ctx = canvas.getContext("2d");
const uiRoot = document.getElementById("ui");
if (!ctx || !uiRoot) {
  throw new Error("Missing canvas or UI root");
}

const session = new GameSession();
const input = new Input(canvas);
let duel: DuelController | null = null;
let bot: TutorialBot | null = null;
let duelActive = false;
let resultShown = false;
/** When the result phase began (for delaying the menu). */
let resultPhaseAt: number | null = null;
/** Wins in the current online series (host left / guest right). Resets on leave. */
let seriesWins = { host: 0, guest: 0 };

function resetSeriesWins(): void {
  seriesWins = { host: 0, guest: 0 };
}
const RESULT_MENU_DELAY_MS = 2000;
/** Tutorial coaching gates already dismissed this round. */
const tutorialGatesDone = new Set<string>();
/** Gate id whose modal is currently open (game paused). */
let tutorialGatePhase: string | null = null;
let tutorialLastFrameMs = 0;

const screens = new Screens(uiRoot, session, {
  onHost: () => {
    resumeAudio();
    startMenuMusic();
    void session.hostLobby();
  },
  onJoin: (code) => {
    resumeAudio();
    startMenuMusic();
    void session.joinLobby(code);
  },
  onQueue: () => {
    resumeAudio();
    startMenuMusic();
    void session.quickMatch();
  },
  onCancelQueue: () => session.cancelQueue(),
  onReady: () => {
    resumeAudio();
    startMenuMusic();
    if (!duel) startDuel();
    duel?.setLocalAbilities(screens.getSelectedAbilities());
    duel?.setLocalReady(true);
    screens.setInDuel(true);
    duelActive = true;
    resultShown = false;
    resultPhaseAt = null;
    input.enable();
  },
  onRematch: () => {
    duel?.setLocalAbilities(screens.getSelectedAbilities());
    duel?.requestRematch();
    resultShown = false;
    resultPhaseAt = null;
    // Skip Face Off — Rematch means you're ready for the next round.
    screens.setInDuel(true);
    duelActive = true;
    input.enable();
  },
  onLeave: () => {
    teardownDuel();
    resetSeriesWins();
    session.leave();
    startMenuMusic();
  },
  onTutorial: () => {
    resumeAudio();
    startMenuMusic();
    startTutorial();
  },
  onTutorialReady: () => {
    resumeAudio();
    if (!duel) return;
    duel.setLocalAbilities(screens.getSelectedAbilities());
    duel.setLocalReady(true);
    screens.setTutorialStage("duel");
    duelActive = true;
    resultShown = false;
    resultPhaseAt = null;
    input.enable();
  },
  onTutorialQuit: () => {
    endTutorial();
    startMenuMusic();
  },
  onTutorialRetry: () => {
    resumeAudio();
    startMenuMusic();
    startTutorial();
  },
  onTutorialContinue: () => {
    resumeAudio();
    if (tutorialGatePhase) {
      tutorialGatesDone.add(tutorialGatePhase);
      tutorialGatePhase = null;
    }
    screens.clearTutorialGate();
    if (duelActive) input.enable();
    // Only the pace-off owns the countdown track — never resume during Hold.
    if (duel?.state.phase === "maneuver") resumeCountdown();
    tutorialLastFrameMs = performance.now();
  },
});

function startTutorial(): void {
  // Drop any online duel without touching the signaling session.
  input.disable();
  duel?.destroy();
  bot?.destroy();
  duel = null;
  bot = null;
  duelActive = false;
  resultShown = false;
  resultPhaseAt = null;
  tutorialGatesDone.clear();
  tutorialGatePhase = null;
  tutorialLastFrameMs = 0;

  const [hostLink, botLink] = createLoopbackPair();
  duel = new DuelController(hostLink, "host", false);
  bot = new TutorialBot(botLink);
  screens.setTutorialStage("abilities");
  openTutorialGate(TUTORIAL_ABILITIES_GATE, TUTORIAL_ABILITIES_TIP, performance.now());
}

function endTutorial(): void {
  input.disable();
  duel?.destroy();
  bot?.destroy();
  duel = null;
  bot = null;
  duelActive = false;
  resultShown = false;
  resultPhaseAt = null;
  tutorialGatesDone.clear();
  tutorialGatePhase = null;
  tutorialLastFrameMs = 0;
  screens.setTutorialStage(null);
}

function startDuel(): void {
  const role = session.getRole();
  if (!role) return;
  duel?.destroy();
  duel = new DuelController(session, role);
  resultShown = false;
  resultPhaseAt = null;
}

function teardownDuel(): void {
  if (screens.isTutorialActive()) return;
  input.disable();
  duel?.destroy();
  duel = null;
  duelActive = false;
  resultShown = false;
  resultPhaseAt = null;
  resetSeriesWins();
  screens.setInDuel(false);
}

session.onState((state) => {
  if (state.phase === "p2p" && !duel) {
    startDuel();
  }
  if (
    (state.phase === "idle" || state.phase === "error") &&
    !screens.isTutorialActive()
  ) {
    teardownDuel();
  }
});

session.onPeerMessage((msg) => {
  if (msg.type === "ready" && msg.ready) {
    if (duel && !duelActive && duel.state.localReady) {
      screens.setInDuel(true);
      duelActive = true;
      input.enable();
    }
  }
  if (msg.type === "match_start") {
    screens.setInDuel(true);
    duelActive = true;
    input.enable();
  }
  // rematch from peer: stay on the rematch menu until we press Rematch ourselves.
  // DuelController records remoteRematchReady / remoteReady.
});

input.onClick((p) => {
  duel?.onArenaClick(p.x, p.y);
});

input.onEmote(() => {
  duel?.tryEmote();
});

input.onAbilityUse(() => {
  duel?.tryPlaceDummy();
});

input.onDebugToggle(() => {
  toggleHitboxDebug();
});

function openTutorialGate(id: string, tip: string, now: number): void {
  tutorialGatePhase = id;
  input.disable();
  pauseCountdown();
  screens.showTutorialGate(tip);
  tutorialLastFrameMs = now;
}

const loop = new GameLoop((now, dt) => {
  if (!duel || !duelActive) {
    renderArenaIdle(ctx);
    return;
  }

  const inTutorial = screens.isTutorialActive();
  const role = inTutorial ? "host" : (session.getRole() ?? "host");

  // Tutorial coaching gate: freeze simulation and stretch match clocks.
  if (inTutorial && tutorialGatePhase) {
    pauseCountdown();
    if (tutorialLastFrameMs > 0) {
      const wall = now - tutorialLastFrameMs;
      duel.shiftClocks(wall);
      bot?.shiftClocks(wall);
    }
    tutorialLastFrameMs = now;
    renderDuel(ctx, duel.state, role, duel);
    return;
  }

  duel.setMouse(input.mouse.x, input.mouse.y);
  duel.tick(now, dt);
  bot?.tick(now, dt);
  renderDuel(
    ctx,
    duel.state,
    role,
    duel,
    inTutorial ? null : seriesWins
  );

  if (inTutorial && duel.state.phase !== "result") {
    const phaseTip = tipForPhase(duel.state.phase);
    if (phaseTip && !tutorialGatesDone.has(duel.state.phase)) {
      openTutorialGate(duel.state.phase, phaseTip, now);
      return;
    }

    // Mid pace-off: mention taunting once the countdown has run a few seconds.
    if (
      duel.state.phase === "maneuver" &&
      duel.state.settleEndMs !== null &&
      !tutorialGatesDone.has(TUTORIAL_TAUNT_GATE)
    ) {
      const intoManeuver = now - duel.state.settleEndMs;
      if (intoManeuver >= TUTORIAL_TAUNT_AT_MS) {
        openTutorialGate(TUTORIAL_TAUNT_GATE, TUTORIAL_TAUNT_TIP, now);
        return;
      }
    }
  }

  tutorialLastFrameMs = now;

  if (duel.state.phase === "result") {
    if (resultPhaseAt === null) {
      resultPhaseAt = now;
      if (!inTutorial) {
        if (duel.state.outcome === "host_win") seriesWins.host += 1;
        else if (duel.state.outcome === "guest_win") seriesWins.guest += 1;
      }
    }
    if (!resultShown && now - resultPhaseAt >= RESULT_MENU_DELAY_MS) {
      resultShown = true;
      const { title, flavor } = resultCopy(
        duel.state.outcome,
        role,
        duel.state.host.hit,
        duel.state.guest.hit
      );
      if (inTutorial) {
        screens.showTutorialResult(title, flavor);
      } else {
        screens.showResult(title, flavor);
      }
      input.disable();
    }
  } else {
    resultPhaseAt = null;
  }
});

const WIN_FLAVOR = [
  "Holy $#@%! You're still standing!",
  "Outdrew 'em fair and square.",
  "Another notch for the belt.",
  "They'll be singing about this one in the saloon.",
  "Quickest hands this side of the Rio Grande.",
  "The other fella's dead.",
];

const LOSE_FLAVOR = [
  "Dead and forgotten.",
  "You bit the dust. How does it taste?",
  "It's a bit more than a flesh wound.",
  "Next time, keep your eyes open.",
  "So sad.",
  "Off to the cemetery you go.",
];

/** Both players got hit. */
const TIE_BOTH_DEAD = [
  "Well, now what?",
  "Suddenly, the town is much quieter.",
  "Even steven... Even steven shed a tear.",
  "I hope it was worth it.",
];

/** Neither player got hit (time ran out / both missed). */
const TIE_BOTH_LIVE = [
  "You both live to see another day.",
  "Seriously? You're both still alive?",
  "That was lame.",
  "Well, now what?",
];

function pickFlavor(lines: string[]): string {
  return lines[Math.floor(Math.random() * lines.length)]!;
}

function resultCopy(
  outcome: import("@duelgame/shared").DuelOutcome | null,
  role: import("@duelgame/shared").Role,
  hostHit: boolean,
  guestHit: boolean
): { title: string; flavor: string } {
  if (!outcome || outcome === "tie") {
    const bothDead = hostHit && guestHit;
    return {
      title: "TIE!",
      flavor: pickFlavor(bothDead ? TIE_BOTH_DEAD : TIE_BOTH_LIVE),
    };
  }
  const youWon =
    (role === "host" && outcome === "host_win") ||
    (role === "guest" && outcome === "guest_win");
  return youWon
    ? { title: "You win!", flavor: pickFlavor(WIN_FLAVOR) }
    : { title: "You lose!", flavor: pickFlavor(LOSE_FLAVOR) };
}

loop.start();
